import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    FormLabel,
    IconButton,
    Input,
    Link,
    Modal,
    ModalDialog,
    Option,
    Select,
    Sheet,
    Stack,
    Tab,
    TabList,
    TabPanel,
    Table,
    Tabs,
    Textarea,
    Tooltip,
    Typography,
} from '@mui/joy';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelIcon from '@mui/icons-material/Cancel';

import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import GeneralApiService from '../../../axios/Axios';

type TaskStatus = 'PENDING' | 'READY' | 'RUNNING' | 'COMPLETED' | 'CANCELLED' | 'HUMAN_INTERVENED';

interface RonaldTask {
    id: number;
    franchise: number;
    franchise_location: string;
    customer: number;
    customer_name: string;
    customer_phone: string;
    lead: number | null;
    booking: number | null;
    task_type: 'MESSAGE_REPLY' | 'SEQUENCE_STEP' | 'FOLLOW_UP';
    task_type_display: string;
    status: TaskStatus;
    status_display: string;
    planned_content: string;
    actual_content: string;
    follow_up_hint: string;
    scheduled_at: string | null;
    executed_at: string | null;
    cancelled_at: string | null;
    cancel_reason: string;
    trigger_message_text: string;
    trigger_message_timestamp: string | null;
    needs_human_intervention: boolean;
    intervention_reason: string;
    intervention_acknowledged: boolean;
    action_taken: string;
    pending_action: { action: string; reason: string } | null;
    created_at: string;
    updated_at: string;
}

interface RonaldKnowledgeEntry {
    id: number;
    context: 'LEADS' | 'BOOKINGS' | 'BOTH';
    category: string;
    title: string;
    content: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

interface RonaldSettings {
    id: number;
    system_prompt: string;
    default_system_prompt: string;
    effective_system_prompt: string;
    created_at: string;
    updated_at: string;
}

interface Franchise {
    id: number;
    location: string;
    [key: string]: any;
}

const STATUS_COLORS: Record<TaskStatus, 'primary' | 'warning' | 'success' | 'danger' | 'neutral'> = {
    PENDING: 'warning',
    READY: 'warning',
    RUNNING: 'primary',
    COMPLETED: 'success',
    CANCELLED: 'neutral',
    HUMAN_INTERVENED: 'danger',
};

const KB_CATEGORIES = [
    ['OBJECTION_HANDLING', 'Objection Handling'],
    ['FAQ', 'FAQ'],
    ['PRICING', 'Pricing'],
    ['SCHEDULING', 'Scheduling'],
    ['SERVICE_INFO', 'Service Info'],
    ['GENERAL', 'General'],
];

const KB_CONTEXTS: Array<{ value: 'LEADS' | 'BOOKINGS' | 'BOTH'; label: string }> = [
    { value: 'BOTH', label: 'Both' },
    { value: 'LEADS', label: 'Leads only' },
    { value: 'BOOKINGS', label: 'Bookings only' },
];

function formatDateTime(iso: string | null): string {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return iso;
    }
}

// ---------------------------------------------------------------------------
// Tasks tab — scope follows the selected location (auto-filters when a
// specific franchise is picked in the sidebar; shows everything on "All").
// ---------------------------------------------------------------------------

function TasksTab({ franchise }: { franchise: Franchise | null }) {
    const [tasks, setTasks] = useState<RonaldTask[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>('ALL_ACTIVE');
    const [selectedTask, setSelectedTask] = useState<RonaldTask | null>(null);

    const fetchTasks = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params: Record<string, string | number> = { limit: 200 };
            if (franchise) params.franchise_id = franchise.id;
            if (statusFilter === 'PENDING') params.status = 'PENDING';
            else if (statusFilter === 'HUMAN_INTERVENED') params.needs_human = 'true';
            else if (statusFilter === 'COMPLETED') params.status = 'COMPLETED';
            else if (statusFilter === 'CANCELLED') params.status = 'CANCELLED';
            const data = await GeneralApiService.listRonaldTasks(params);
            setTasks(Array.isArray(data) ? data : []);
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Failed to load Ronald tasks');
        } finally {
            setLoading(false);
        }
    }, [franchise, statusFilter]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    const handleCancel = useCallback(async (task: RonaldTask) => {
        const reason = window.prompt('Cancellation reason (optional):', '') || '';
        try {
            await GeneralApiService.cancelRonaldTask(task.id, reason);
            await fetchTasks();
        } catch (err: any) {
            alert(err?.response?.data?.detail || 'Failed to cancel task');
        }
    }, [fetchTasks]);

    const handleForceExecute = useCallback(async (task: RonaldTask) => {
        if (!window.confirm('Force Ronald to execute this task now?')) return;
        try {
            await GeneralApiService.forceExecuteRonaldTask(task.id);
            await fetchTasks();
        } catch (err: any) {
            alert(err?.response?.data?.detail || 'Failed to force execute');
        }
    }, [fetchTasks]);

    return (
        <Box>
            <Stack direction="row" spacing={2} alignItems="flex-end" sx={{ mb: 2 }}>
                <FormControl size="sm" sx={{ minWidth: 220 }}>
                    <FormLabel>Filter</FormLabel>
                    <Select value={statusFilter} onChange={(_, v) => v && setStatusFilter(v as string)}>
                        <Option value="ALL_ACTIVE">All tasks</Option>
                        <Option value="PENDING">Pending</Option>
                        <Option value="HUMAN_INTERVENED">Needs human review</Option>
                        <Option value="COMPLETED">Completed</Option>
                        <Option value="CANCELLED">Cancelled</Option>
                    </Select>
                </FormControl>
                <Button
                    size="sm"
                    variant="soft"
                    startDecorator={<RefreshIcon />}
                    onClick={fetchTasks}
                    disabled={loading}
                >
                    Refresh
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                {loading && <CircularProgress size="sm" />}
            </Stack>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                <Table size="sm" stickyHeader hoverRow>
                    <thead>
                        <tr>
                            <th style={{ width: '60px' }}>ID</th>
                            <th>Customer</th>
                            <th>Franchise</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Scheduled</th>
                            <th>Preview</th>
                            <th style={{ width: '200px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tasks.length === 0 && !loading && (
                            <tr>
                                <td colSpan={8} style={{ textAlign: 'center', padding: '24px' }}>
                                    <Typography color="neutral" level="body-sm">No tasks match this filter.</Typography>
                                </td>
                            </tr>
                        )}
                        {tasks.map((task) => {
                            const preview = (task.actual_content || task.planned_content || task.trigger_message_text || '').slice(0, 80);
                            return (
                                <tr
                                    key={task.id}
                                    onClick={() => setSelectedTask(task)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td><Typography level="body-sm">#{task.id}</Typography></td>
                                    <td>
                                        <Typography level="body-sm" fontWeight="md">{task.customer_name || '—'}</Typography>
                                        <Typography level="body-xs" color="neutral">{task.customer_phone}</Typography>
                                    </td>
                                    <td><Typography level="body-xs">{task.franchise_location}</Typography></td>
                                    <td><Typography level="body-xs">{task.task_type_display}</Typography></td>
                                    <td>
                                        <Chip size="sm" color={STATUS_COLORS[task.status] || 'neutral'} variant="soft">
                                            {task.status_display}
                                        </Chip>
                                        {task.needs_human_intervention && !task.intervention_acknowledged && (
                                            <Chip size="sm" color="danger" variant="solid" sx={{ ml: 0.5 }}>Needs review</Chip>
                                        )}
                                    </td>
                                    <td><Typography level="body-xs">{formatDateTime(task.scheduled_at)}</Typography></td>
                                    <td>
                                        <Tooltip title={preview}>
                                            <Typography level="body-xs" noWrap sx={{ maxWidth: 240 }}>{preview}</Typography>
                                        </Tooltip>
                                    </td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <Stack direction="row" spacing={0.5}>
                                            {task.status === 'PENDING' && (
                                                <>
                                                    <Tooltip title="Run now">
                                                        <IconButton size="sm" variant="soft" color="primary" onClick={() => handleForceExecute(task)}>
                                                            <PlayArrowIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Cancel">
                                                        <IconButton size="sm" variant="soft" color="danger" onClick={() => handleCancel(task)}>
                                                            <CancelIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Stack>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </Sheet>

            <Modal open={!!selectedTask} onClose={() => setSelectedTask(null)}>
                <ModalDialog layout="center" sx={{ maxWidth: 720, width: '90vw' }}>
                    {selectedTask && (
                        <Box>
                            <Typography level="title-lg" sx={{ mb: 1 }}>
                                Task #{selectedTask.id} — {selectedTask.task_type_display}
                            </Typography>
                            <Chip size="sm" color={STATUS_COLORS[selectedTask.status] || 'neutral'} variant="soft" sx={{ mb: 2 }}>
                                {selectedTask.status_display}
                            </Chip>
                            <Stack spacing={1.5}>
                                <Box>
                                    <Typography level="body-xs" color="neutral">Customer</Typography>
                                    <Typography level="body-sm">{selectedTask.customer_name} ({selectedTask.customer_phone})</Typography>
                                </Box>
                                <Box>
                                    <Typography level="body-xs" color="neutral">Franchise</Typography>
                                    <Typography level="body-sm">{selectedTask.franchise_location}</Typography>
                                </Box>
                                <Box>
                                    <Typography level="body-xs" color="neutral">Trigger message</Typography>
                                    <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedTask.trigger_message_text || '—'}
                                    </Typography>
                                    <Typography level="body-xs" color="neutral">
                                        Received {formatDateTime(selectedTask.trigger_message_timestamp)}
                                    </Typography>
                                </Box>
                                {selectedTask.planned_content && (
                                    <Box>
                                        <Typography level="body-xs" color="neutral">Planned reply</Typography>
                                        <Sheet variant="soft" sx={{ p: 1, borderRadius: 'sm' }}>
                                            <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>{selectedTask.planned_content}</Typography>
                                        </Sheet>
                                    </Box>
                                )}
                                {selectedTask.actual_content && (
                                    <Box>
                                        <Typography level="body-xs" color="neutral">Sent reply</Typography>
                                        <Sheet variant="soft" color="success" sx={{ p: 1, borderRadius: 'sm' }}>
                                            <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>{selectedTask.actual_content}</Typography>
                                        </Sheet>
                                    </Box>
                                )}
                                {selectedTask.needs_human_intervention && (
                                    <Alert color="danger" variant="soft">
                                        <Box>
                                            <Typography level="title-sm">Human review required</Typography>
                                            <Typography level="body-sm">{selectedTask.intervention_reason || 'No reason given'}</Typography>
                                        </Box>
                                    </Alert>
                                )}
                                {selectedTask.cancel_reason && (
                                    <Box>
                                        <Typography level="body-xs" color="neutral">Cancelled because</Typography>
                                        <Typography level="body-sm">{selectedTask.cancel_reason}</Typography>
                                    </Box>
                                )}
                                {selectedTask.action_taken && (
                                    <Alert color="warning" variant="soft">
                                        <Box>
                                            <Typography level="title-sm">Action executed</Typography>
                                            <Typography level="body-sm">
                                                {selectedTask.action_taken === 'cancel_booking' ? 'Cancelled booking' :
                                                 selectedTask.action_taken === 'mark_needs_rescheduling' ? 'Flagged booking for rescheduling' :
                                                 selectedTask.action_taken}
                                            </Typography>
                                        </Box>
                                    </Alert>
                                )}
                                <Box>
                                    <Typography level="body-xs" color="neutral">Timeline</Typography>
                                    <Typography level="body-xs">Created: {formatDateTime(selectedTask.created_at)}</Typography>
                                    <Typography level="body-xs">Scheduled: {formatDateTime(selectedTask.scheduled_at)}</Typography>
                                    <Typography level="body-xs">Executed: {formatDateTime(selectedTask.executed_at)}</Typography>
                                </Box>
                            </Stack>
                            <Stack direction="row" spacing={1} sx={{ mt: 3, justifyContent: 'flex-end' }}>
                                <Button variant="plain" onClick={() => setSelectedTask(null)}>Close</Button>
                            </Stack>
                        </Box>
                    )}
                </ModalDialog>
            </Modal>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Knowledge Base tab — global; same entries drive every franchise.
// ---------------------------------------------------------------------------

function KnowledgeBaseTab() {
    const [entries, setEntries] = useState<RonaldKnowledgeEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState<RonaldKnowledgeEntry | null>(null);
    const [creating, setCreating] = useState(false);

    const fetchEntries = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await GeneralApiService.listRonaldKnowledge();
            setEntries(Array.isArray(data) ? data : []);
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Failed to load knowledge base');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchEntries(); }, [fetchEntries]);

    const handleDelete = useCallback(async (entry: RonaldKnowledgeEntry) => {
        if (!window.confirm(`Delete "${entry.title}"? This cannot be undone.`)) return;
        try {
            await GeneralApiService.deleteRonaldKnowledge(entry.id);
            await fetchEntries();
        } catch (err: any) {
            alert(err?.response?.data?.detail || 'Failed to delete entry');
        }
    }, [fetchEntries]);

    return (
        <Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Button
                    size="sm"
                    variant="solid"
                    color="primary"
                    startDecorator={<AddIcon />}
                    onClick={() => setCreating(true)}
                >
                    Add entry
                </Button>
                <Button
                    size="sm"
                    variant="soft"
                    startDecorator={<RefreshIcon />}
                    onClick={fetchEntries}
                    disabled={loading}
                >
                    Refresh
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                {loading && <CircularProgress size="sm" />}
            </Stack>

            <Alert color="neutral" variant="soft" sx={{ mb: 2 }}>
                <Typography level="body-sm">
                    Ronald's knowledge base is shared across every franchise. Franchise-specific facts
                    (phone, email, owner, availability) are added automatically at reply time — you
                    don't need to repeat them here.
                </Typography>
            </Alert>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                <Table size="sm" stickyHeader>
                    <thead>
                        <tr>
                            <th style={{ width: '60px' }}>ID</th>
                            <th>Title</th>
                            <th>Category</th>
                            <th>Context</th>
                            <th>Active</th>
                            <th style={{ width: '140px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.length === 0 && !loading && (
                            <tr>
                                <td colSpan={6} style={{ textAlign: 'center', padding: '24px' }}>
                                    <Typography color="neutral" level="body-sm">No knowledge base entries yet.</Typography>
                                </td>
                            </tr>
                        )}
                        {entries.map((entry) => (
                            <tr key={entry.id}>
                                <td><Typography level="body-sm">#{entry.id}</Typography></td>
                                <td>
                                    <Typography level="body-sm" fontWeight="md">{entry.title}</Typography>
                                    <Typography level="body-xs" color="neutral" noWrap sx={{ maxWidth: 380 }}>
                                        {entry.content.slice(0, 120)}
                                    </Typography>
                                </td>
                                <td><Chip size="sm" variant="soft">{entry.category}</Chip></td>
                                <td><Typography level="body-xs">{entry.context}</Typography></td>
                                <td>
                                    <Chip size="sm" color={entry.is_active ? 'success' : 'neutral'} variant="soft">
                                        {entry.is_active ? 'Active' : 'Inactive'}
                                    </Chip>
                                </td>
                                <td>
                                    <Stack direction="row" spacing={0.5}>
                                        <IconButton size="sm" variant="soft" onClick={() => setEditing(entry)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="sm" variant="soft" color="danger" onClick={() => handleDelete(entry)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Sheet>

            {(creating || editing) && (
                <KnowledgeEntryModal
                    entry={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSaved={() => { setCreating(false); setEditing(null); fetchEntries(); }}
                />
            )}
        </Box>
    );
}

function KnowledgeEntryModal({
    entry, onClose, onSaved,
}: {
    entry: RonaldKnowledgeEntry | null;
    onClose: () => void;
    onSaved: () => void;
}) {
    const [title, setTitle] = useState(entry?.title || '');
    const [content, setContent] = useState(entry?.content || '');
    const [category, setCategory] = useState(entry?.category || 'GENERAL');
    const [context, setContext] = useState<'LEADS' | 'BOOKINGS' | 'BOTH'>(entry?.context || 'BOTH');
    const [isActive, setIsActive] = useState(entry?.is_active ?? true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSave = async () => {
        if (!title.trim() || !content.trim()) {
            setError('Title and content are required.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                title: title.trim(),
                content: content.trim(),
                category,
                context,
                is_active: isActive,
            };
            if (entry) {
                await GeneralApiService.updateRonaldKnowledge(entry.id, payload);
            } else {
                await GeneralApiService.createRonaldKnowledge(payload);
            }
            onSaved();
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Failed to save entry');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open onClose={onClose}>
            <ModalDialog layout="center" sx={{ maxWidth: 640, width: '90vw' }}>
                <Typography level="title-lg" sx={{ mb: 2 }}>
                    {entry ? 'Edit knowledge entry' : 'Add knowledge entry'}
                </Typography>
                <Stack spacing={2}>
                    <FormControl>
                        <FormLabel>Title</FormLabel>
                        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
                    </FormControl>
                    <FormControl>
                        <FormLabel>Content</FormLabel>
                        <Textarea minRows={6} value={content} onChange={(e) => setContent(e.target.value)} />
                        <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                            Variables: {'{arrival_time}'} is replaced with the franchise's arrival time from Franchise Settings (e.g. 9am).
                        </Typography>
                    </FormControl>
                    <Stack direction="row" spacing={2}>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Category</FormLabel>
                            <Select value={category} onChange={(_, v) => v && setCategory(v as string)}>
                                {KB_CATEGORIES.map(([val, label]) => (
                                    <Option key={val} value={val}>{label}</Option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ flex: 1 }}>
                            <FormLabel>Applies to</FormLabel>
                            <Select value={context} onChange={(_, v) => v && setContext(v as any)}>
                                {KB_CONTEXTS.map((c) => (
                                    <Option key={c.value} value={c.value}>{c.label}</Option>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <FormControl orientation="horizontal">
                        <FormLabel>Active</FormLabel>
                        <Select value={isActive ? '1' : '0'} onChange={(_, v) => setIsActive(v === '1')}>
                            <Option value="1">Active</Option>
                            <Option value="0">Inactive</Option>
                        </Select>
                    </FormControl>
                    {error && <Alert color="danger">{error}</Alert>}
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 3, justifyContent: 'flex-end' }}>
                    <Button variant="plain" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button color="primary" onClick={handleSave} loading={saving}>Save</Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}

// ---------------------------------------------------------------------------
// Settings tab — global singleton; one system prompt for all of YetiClean.
// ---------------------------------------------------------------------------

function SettingsTab() {
    const [settings, setSettings] = useState<RonaldSettings | null>(null);
    const [prompt, setPrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const fetchSettings = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data: RonaldSettings = await GeneralApiService.getRonaldSettings();
            setSettings(data);
            const saved = (data?.system_prompt || '').trim();
            setPrompt(saved || data?.default_system_prompt || '');
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Failed to load settings');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchSettings(); }, [fetchSettings]);

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccess(false);
        try {
            const payload = prompt.trim() === (settings?.default_system_prompt || '').trim()
                ? { system_prompt: '' }
                : { system_prompt: prompt };
            const data: RonaldSettings = await GeneralApiService.updateRonaldSettings(payload);
            setSettings(data);
            const saved = (data?.system_prompt || '').trim();
            setPrompt(saved || data?.default_system_prompt || '');
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleResetToDefault = () => {
        setPrompt(settings?.default_system_prompt || '');
    };

    const handleRevert = () => {
        const saved = (settings?.system_prompt || '').trim();
        setPrompt(saved || settings?.default_system_prompt || '');
    };

    const isCustomised = Boolean((settings?.system_prompt || '').trim());

    return (
        <Box>
            {loading && <CircularProgress size="sm" />}
            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert color="success" sx={{ mb: 2 }}>Settings saved.</Alert>}

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Chip size="sm" color={isCustomised ? 'primary' : 'neutral'} variant="soft">
                    {isCustomised ? 'Custom prompt' : 'Using default'}
                </Chip>
            </Stack>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                This system prompt is the editable prefix of what Ronald sees before every reply —
                one prompt for the entire business. The default is pre-filled below; edit and save
                to override, or use <b>Reset to default</b> to restore it. Dynamic context
                (franchise details for the customer's location, knowledge base, conversation
                history, availability, and JSON schema) is appended automatically.
            </Typography>

            <FormControl>
                <FormLabel>System prompt prefix</FormLabel>
                <Textarea
                    minRows={18}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Loading default prompt..."
                    disabled={loading}
                />
            </FormControl>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                <Button color="primary" onClick={handleSave} loading={saving}>
                    Save
                </Button>
                <Button variant="plain" onClick={handleRevert} disabled={saving}>
                    Revert unsaved changes
                </Button>
                <Button variant="outlined" color="neutral" onClick={handleResetToDefault} disabled={saving}>
                    Reset to default
                </Button>
            </Stack>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Main page — global admin. Tasks tab still respects the selected location
// (so picking a franchise in the sidebar filters the list).
// ---------------------------------------------------------------------------

export default function Ronald() {
    const [franchise, setFranchise] = useState<Franchise | null>(null);
    const [tabIndex, setTabIndex] = useState(0);

    const selectedLocation = useMemo(() => localStorage.getItem('location'), []);
    const isAllLocations = !selectedLocation || selectedLocation === 'All';

    useEffect(() => {
        if (isAllLocations) {
            setFranchise(null);
            return;
        }
        GeneralApiService.getFranchiseByName()
            .then((data) => {
                if (data && data.id) {
                    setFranchise(data);
                } else {
                    setFranchise(null);
                }
            })
            .catch(() => setFranchise(null));
    }, [isAllLocations]);

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Breadcrumbs size="sm" aria-label="breadcrumbs" separator={<ChevronRightRoundedIcon />} sx={{ pl: 0 }}>
                    <Link underline="none" color="neutral" href="/dashboard" aria-label="Home">
                        <HomeRoundedIcon />
                    </Link>
                    <Link underline="hover" color="neutral" href="/dashboard" fontSize={12} fontWeight={500}>
                        Dashboard
                    </Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>
                        Ronald AI
                    </Typography>
                </Breadcrumbs>
            </Box>

            <Box sx={{ display: 'flex', mb: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <SmartToyIcon sx={{ fontSize: 32, color: 'primary.plainColor' }} />
                    <Typography level="h2" component="h1">Ronald AI</Typography>
                    <Chip size="md" variant="soft" color="primary" sx={{ ml: 2 }}>
                        Global
                    </Chip>
                    {franchise && (
                        <Chip size="sm" variant="outlined" color="neutral">
                            Tasks filtered to {franchise.location}
                        </Chip>
                    )}
                </Stack>
            </Box>

            <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v as number)}>
                <TabList>
                    <Tab value={0}>Tasks</Tab>
                    <Tab value={1}>Knowledge Base</Tab>
                    <Tab value={2}>Settings</Tab>
                </TabList>
                <TabPanel value={0} sx={{ pt: 2 }}>
                    <TasksTab franchise={franchise} />
                </TabPanel>
                <TabPanel value={1} sx={{ pt: 2 }}>
                    <KnowledgeBaseTab />
                </TabPanel>
                <TabPanel value={2} sx={{ pt: 2 }}>
                    <SettingsTab />
                </TabPanel>
            </Tabs>
        </>
    );
}
